import { useCallback } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface DataTablePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export function DataTablePagination({
  currentPage,
  totalPages,
  onPageChange,
  className = "",
}: DataTablePaginationProps) {
  const handlePageChange = useCallback(
    (page: number) => {
      onPageChange(page);
    },
    [onPageChange],
  );

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className={`mt-4 ${className}`}>
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (currentPage > 0) handlePageChange(currentPage - 1);
              }}
              isDisabled={currentPage === 0}
            />
          </PaginationItem>

          {(() => {
            if (totalPages <= 7) {
              return Array.from({ length: totalPages }).map((_, idx) => (
                <PaginationItem key={idx}>
                  <PaginationLink
                    href="#"
                    isActive={currentPage === idx}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(idx);
                    }}
                  >
                    {idx + 1}
                  </PaginationLink>
                </PaginationItem>
              ));
            }

            const pageLinks = [];
            const window = 2;
            const start = Math.max(2, currentPage + 1 - window);
            const end = Math.min(totalPages - 1, currentPage + 1 + window);

            // First page
            pageLinks.push(
              <PaginationItem key={1}>
                <PaginationLink
                  href="#"
                  isActive={currentPage === 0}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(0);
                  }}
                >
                  1
                </PaginationLink>
              </PaginationItem>,
            );

            // Start ellipsis
            if (start > 2) {
              pageLinks.push(
                <PaginationItem key="start-ellipsis">
                  <span className="px-2">...</span>
                </PaginationItem>,
              );
            }

            // Middle pages
            for (let i = start; i <= end; i++) {
              pageLinks.push(
                <PaginationItem key={i}>
                  <PaginationLink
                    href="#"
                    isActive={currentPage === i - 1}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(i - 1);
                    }}
                  >
                    {i}
                  </PaginationLink>
                </PaginationItem>,
              );
            }

            // End ellipsis
            if (end < totalPages - 1) {
              pageLinks.push(
                <PaginationItem key="end-ellipsis">
                  <span className="px-2">...</span>
                </PaginationItem>,
              );
            }

            // Last page
            pageLinks.push(
              <PaginationItem key={totalPages}>
                <PaginationLink
                  href="#"
                  isActive={currentPage === totalPages - 1}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(totalPages - 1);
                  }}
                >
                  {totalPages}
                </PaginationLink>
              </PaginationItem>,
            );

            return pageLinks;
          })()}

          <PaginationItem>
            <PaginationNext
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (currentPage < totalPages - 1)
                  handlePageChange(currentPage + 1);
              }}
              isDisabled={currentPage === totalPages - 1}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}
